import React, { useState, useEffect } from 'react';
import { PageLoading } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SubmissionForm from './components/SubmissionForm';
import ConferenceBrief from './components/ConferenceBrief';
import { conferenceService } from '../../services/conferenceService';
import { submissionService } from '../../services/submissionService';
import { supabase } from '../../lib/supabase';

const AuthorSubmissionPortal = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('submissions');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const conferenceId = searchParams?.get('conferenceId');
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [conferenceInfo, setConferenceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);

  // Fetch the conference this portal is submitting into
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setPageError(null);

        if (!conferenceId) {
          setPageError(t('authorPortal.noConferenceSelected'));
          return;
        }

        // Fetch conference details
        const conference = await conferenceService?.getById(conferenceId);
        setConferenceInfo({
          id: conference?.id,
          name: conference?.shortName || conference?.name,
          deadline: conference?.paperDeadline,
          notificationDate: conference?.decisionDate,
          // Already returned by getById and previously dropped on the floor —
          // the brief panel shows the whole timeline, not just the deadline.
          reviewDeadline: conference?.reviewDeadline,
          cameraReadyDeadline: conference?.cameraReadyDeadline,
          startDate: conference?.startDate,
          endDate: conference?.endDate,
          allowedFileTypes: conference?.allowedFileTypes || 'both'
        });

      } catch (err) {
        console.error('Failed to fetch data:', err);
        setPageError(err?.message || t('authorPortal.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [conferenceId]);

  const handleSubmit = async (formData) => {
    try {
      setIsSaving(true);
      setSubmissionError(null);

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error(t('authorPortal.notAuthenticated'));

      const conference = await conferenceService?.getById(conferenceId);
      const participantNumber = await submissionService?.getOrCreateConferenceParticipantNumber(conferenceId, user?.id);
      const participantId = submissionService?.formatConferenceParticipantId(
        conference?.shortName || conference?.name,
        participantNumber
      );

      // Upload the main submission file
      const submissionFileUpload = await submissionService?.uploadSubmissionFile(formData?.pdfFile, user?.id, conferenceId);

      // Create submission
      await submissionService?.create({
        conferenceId: conferenceId,
        trackId: formData?.trackId,
        title: formData?.title,
        abstract: formData?.abstract,
        keywords: formData?.keywords,
        pdfUrl: submissionFileUpload?.path,
        pdfFileName: submissionFileUpload?.fileName,
        pdfFileSize: submissionFileUpload?.fileSize,
        coAuthors: formData?.coAuthors,
        status: 'submitted'
      });

      // Send email notification to author
      try {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('first_name, last_name, email')
          ?.eq('id', user?.id)
          ?.single();

        if (profile?.email && conference) {
          const { notificationService } = await import('../../services/notificationService');
          await notificationService?.sendSubmissionConfirmationEmail(
            profile?.email,
            `${profile?.first_name} ${profile?.last_name}`?.trim(),
            formData?.title,
            conference?.name,
            participantId,
            conferenceId
          );
        }
      } catch (emailError) {
        console.error('Failed to send submission confirmation email:', emailError);
        // Don't fail the submission if email fails
      }

      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        // Navigate back to dashboard with refresh flag
        navigate('/user-dashboard', { state: { refresh: true } });
      }, 3000);
    } catch (err) {
      console.error('Submission error:', err);
      setSubmissionError(err?.message || t('authorPortal.submitError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async (formData) => {
    try {
      setIsSaving(true);
      setSubmissionError(null);

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error(t('authorPortal.notAuthenticated'));

      let submissionFileData = {};
      if (formData?.pdfFile) {
        const submissionFileUpload = await submissionService?.uploadSubmissionFile(formData?.pdfFile, user?.id, conferenceId);
        submissionFileData = {
          pdfUrl: submissionFileUpload?.path,
          pdfFileName: submissionFileUpload?.fileName,
          pdfFileSize: submissionFileUpload?.fileSize
        };
      }

      await submissionService?.create({
        conferenceId: conferenceId,
        trackId: formData?.trackId,
        title: formData?.title,
        abstract: formData?.abstract,
        keywords: formData?.keywords,
        ...submissionFileData,
        coAuthors: formData?.coAuthors,
        status: 'draft'
      });

      toastSuccess(t('authorPortal.saveDraft.success'));
    } catch (err) {
      console.error('Save draft error:', err);
      setSubmissionError(err?.message || t('authorPortal.saveDraft.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <PageLoading label={t('authorPortal.loading')} />
      </div>
    );
  }

  if (pageError || !conferenceInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">{t('authorPortal.errorTitle')}</h2>
          <p className="text-muted-foreground mb-4">{pageError || t('authorPortal.errorDefault')}</p>
          <Button onClick={() => navigate('/public-conference-board')}>{t('authorPortal.browseConferences')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        <div className="mb-6 md:mb-8">
          <Button
            variant="ghost"
            size="default"
            onClick={() => navigate('/user-dashboard')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            {t('authorPortal.backToDashboard')}
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                {t('authorPortal.title')}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {t('authorPortal.portalSubtitle', { name: conferenceInfo?.name })}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-error/10 border border-error/20 rounded-lg">
                <Icon name="Clock" size={20} className="text-error" />
                <div>
                  <p className="text-xs text-muted-foreground">{t('authorPortal.deadline')}</p>
                  <p className="text-sm font-semibold text-foreground">
                    {conferenceInfo?.deadline ? new Date(conferenceInfo.deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : t('authorPortal.deadlineTba')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {submissionError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-error text-sm">{submissionError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <SubmissionForm
                conferenceId={conferenceId}
                onSubmit={handleSubmit}
                isSaving={isSaving}
                allowedFileTypes={conferenceInfo?.allowedFileTypes || 'both'}
              />
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-20">
                <ConferenceBrief conference={conferenceInfo} />
              </div>
            </div>
          </div>
        </div>
      </main>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-elevation-5 p-6 md:p-8 max-w-md w-full animate-in fade-in zoom-in duration-250">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle2" size={40} className="text-success" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
                {t('authorPortal.successModal.title')}
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                {t('authorPortal.successModal.message')}
              </p>
              <Button
                variant="default"
                size="lg"
                onClick={() => setShowSuccessModal(false)}
                fullWidth
              >
                {t('authorPortal.successModal.continue')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorSubmissionPortal;
